import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { HelpCircle, ArrowLeft, ArrowRight, X, ExternalLink, RefreshCw } from "lucide-react";

/**
 * Visual step-by-step guide to unblock camera permission.
 * Auto-detects browser + OS and shows tailored steps with SVG mockups.
 */

function detectPlatform() {
  const ua = navigator.userAgent;
  const isIOS = /iP(hone|ad|od)/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/Chrome|CriOS|EdgiOS|FxiOS/i.test(ua);
  const isChrome = /Chrome|CriOS/i.test(ua) && !/Edg|OPR/i.test(ua);
  const isEdge = /Edg/i.test(ua);
  const isFirefox = /Firefox|FxiOS/i.test(ua);
  if (isIOS) return isSafari ? "ios-safari" : "ios-chrome";
  if (isAndroid) return isChrome ? "android-chrome" : "android-other";
  if (isSafari) return "mac-safari";
  if (isFirefox) return "desktop-firefox";
  if (isEdge) return "desktop-edge";
  return "desktop-chrome";
}

/* ---------- SVG ILLUSTRATIONS ---------- */

const AnimatedPulse = ({ cx, cy, r = 12, color = "#f59e0b" }) => (
  <>
    <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth="2" opacity="0.9">
      <animate attributeName="r" values={`${r};${r + 8};${r}`} dur="1.5s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.9;0;0.9" dur="1.5s" repeatCount="indefinite" />
    </circle>
    <circle cx={cx} cy={cy} r={r - 4} fill="none" stroke={color} strokeWidth="3" />
  </>
);

/* Address bar with padlock (Chrome/Edge Desktop and Android) */
const BrowserAddressBarSvg = ({ highlight = "lock" }) => (
  <svg viewBox="0 0 400 90" className="w-full max-w-md mx-auto">
    <rect x="0" y="0" width="400" height="90" rx="8" fill="#f1f5f9" />
    {/* Tab bar */}
    <rect x="10" y="8" width="130" height="26" rx="6" fill="#fff" />
    <text x="30" y="26" fontSize="11" fill="#475569" fontFamily="Arial">plastiq-mes</text>
    {/* Address bar */}
    <rect x="20" y="46" width="360" height="34" rx="17" fill="#fff" stroke="#e2e8f0" />
    {/* Lock icon */}
    <g transform="translate(38, 55)">
      <rect x="0" y="6" width="16" height="12" rx="2" fill="#16a34a" />
      <path d="M3 6 V4 A5 5 0 0 1 13 4 V6" fill="none" stroke="#16a34a" strokeWidth="2" />
    </g>
    <text x="65" y="68" fontSize="11" fill="#64748b" fontFamily="Arial">plastiq-mes.emergent.host</text>
    {highlight === "lock" && <AnimatedPulse cx={46} cy={63} r={14} />}
  </svg>
);

/* Permission dropdown after clicking padlock */
const PermissionDropdownSvg = ({ highlight = "camera" }) => (
  <svg viewBox="0 0 400 220" className="w-full max-w-md mx-auto">
    <rect x="0" y="0" width="400" height="40" rx="6" fill="#f1f5f9" />
    <g transform="translate(28, 15)">
      <rect x="0" y="0" width="14" height="10" rx="2" fill="#16a34a" />
      <path d="M2.5 0 V-2 A4 4 0 0 1 11.5 -2 V0" fill="none" stroke="#16a34a" strokeWidth="1.5" />
    </g>
    <text x="60" y="24" fontSize="11" fill="#64748b" fontFamily="Arial">plastiq-mes.emergent.host</text>
    {/* Dropdown card */}
    <rect x="20" y="45" width="330" height="165" rx="10" fill="#fff" stroke="#e2e8f0" strokeWidth="1" />
    <text x="36" y="70" fontSize="12" fontWeight="700" fill="#0f172a" fontFamily="Arial">Ligação segura</text>
    <text x="36" y="86" fontSize="10" fill="#64748b" fontFamily="Arial">A sua ligação é privada.</text>
    <line x1="30" y1="100" x2="340" y2="100" stroke="#e2e8f0" />
    {/* Permissions list */}
    <text x="36" y="118" fontSize="11" fontWeight="700" fill="#0f172a" fontFamily="Arial">Permissões</text>
    {/* Camera row */}
    <g transform="translate(30, 130)">
      <rect x="0" y="0" width="310" height="30" rx="6" fill={highlight === "camera" ? "#fef3c7" : "transparent"} />
      <path d="M16 12 h14 l4 -4 v20 l-4 -4 h-14 z" fill="#64748b" />
      <text x="46" y="20" fontSize="11" fill="#0f172a" fontFamily="Arial">Câmara</text>
      <rect x="240" y="6" width="60" height="18" rx="9" fill="#16a34a" />
      <text x="270" y="19" fontSize="10" fill="#fff" fontFamily="Arial" textAnchor="middle">Permitir</text>
    </g>
    {/* Microphone row */}
    <g transform="translate(30, 168)" opacity="0.5">
      <path d="M20 8 v14 M14 22 h12 M18 22 v6" stroke="#64748b" strokeWidth="1.5" />
      <text x="46" y="20" fontSize="11" fill="#0f172a" fontFamily="Arial">Microfone</text>
    </g>
    {highlight === "camera" && <AnimatedPulse cx={185} cy={145} r={16} />}
  </svg>
);

/* iOS Settings app path: Settings → Safari → Camera */
const IosSettingsSvg = ({ step = 1 }) => (
  <svg viewBox="0 0 400 250" className="w-full max-w-md mx-auto">
    {/* Phone frame */}
    <rect x="80" y="10" width="240" height="230" rx="20" fill="#0f172a" />
    <rect x="90" y="30" width="220" height="200" rx="6" fill="#f8fafc" />
    {step === 1 && (
      <>
        <text x="200" y="52" fontSize="12" fontWeight="700" fill="#0f172a" fontFamily="Arial" textAnchor="middle">Definições</text>
        <line x1="100" y1="60" x2="300" y2="60" stroke="#e2e8f0" />
        {/* Safari row */}
        <g transform="translate(100, 75)">
          <rect x="0" y="0" width="200" height="30" rx="4" fill="#dbeafe" />
          <rect x="8" y="6" width="18" height="18" rx="4" fill="#0ea5e9" />
          <path d="M17 15 l4 -4 l-2 8 l-4 4 z" fill="#fff" />
          <text x="36" y="20" fontSize="12" fontWeight="600" fill="#0f172a" fontFamily="Arial">Safari</text>
          <text x="188" y="19" fontSize="12" fill="#94a3b8" fontFamily="Arial" textAnchor="end">›</text>
        </g>
        {/* Other rows */}
        <g opacity="0.4">
          <text x="108" y="130" fontSize="11" fill="#334155" fontFamily="Arial">Notificações</text>
          <text x="108" y="152" fontSize="11" fill="#334155" fontFamily="Arial">Bateria</text>
          <text x="108" y="174" fontSize="11" fill="#334155" fontFamily="Arial">Privacidade</text>
        </g>
        <AnimatedPulse cx={200} cy={90} r={18} />
      </>
    )}
    {step === 2 && (
      <>
        <text x="200" y="52" fontSize="12" fontWeight="700" fill="#0f172a" fontFamily="Arial" textAnchor="middle">Safari</text>
        <line x1="100" y1="60" x2="300" y2="60" stroke="#e2e8f0" />
        <text x="108" y="80" fontSize="9" fontWeight="700" fill="#64748b" fontFamily="Arial">DEFINIÇÕES SITES</text>
        {/* Camera row */}
        <g transform="translate(100, 90)">
          <rect x="0" y="0" width="200" height="30" rx="4" fill="#dbeafe" />
          <path d="M14 12 h14 l4 -4 v20 l-4 -4 h-14 z" fill="#0ea5e9" />
          <text x="44" y="20" fontSize="12" fontWeight="600" fill="#0f172a" fontFamily="Arial">Câmara</text>
          <text x="188" y="19" fontSize="12" fill="#94a3b8" fontFamily="Arial" textAnchor="end">›</text>
        </g>
        <g opacity="0.4">
          <text x="108" y="145" fontSize="11" fill="#334155" fontFamily="Arial">Microfone</text>
          <text x="108" y="167" fontSize="11" fill="#334155" fontFamily="Arial">Localização</text>
        </g>
        <AnimatedPulse cx={200} cy={105} r={18} />
      </>
    )}
    {step === 3 && (
      <>
        <text x="200" y="52" fontSize="12" fontWeight="700" fill="#0f172a" fontFamily="Arial" textAnchor="middle">Câmara</text>
        <line x1="100" y1="60" x2="300" y2="60" stroke="#e2e8f0" />
        <text x="200" y="80" fontSize="10" fill="#64748b" fontFamily="Arial" textAnchor="middle">Permitir acesso à câmara em Safari</text>
        <g transform="translate(105, 95)">
          <rect x="0" y="0" width="190" height="28" rx="4" fill="#f1f5f9" />
          <circle cx="14" cy="14" r="7" fill="#94a3b8" />
          <text x="30" y="18" fontSize="11" fill="#0f172a" fontFamily="Arial">Perguntar</text>
        </g>
        <g transform="translate(105, 128)">
          <rect x="0" y="0" width="190" height="28" rx="4" fill="#dcfce7" />
          <circle cx="14" cy="14" r="7" fill="#16a34a" />
          <path d="M10 14 l3 3 l6 -6" stroke="#fff" strokeWidth="2" fill="none" />
          <text x="30" y="18" fontSize="11" fontWeight="700" fill="#0f172a" fontFamily="Arial">Permitir</text>
        </g>
        <g transform="translate(105, 161)" opacity="0.5">
          <rect x="0" y="0" width="190" height="28" rx="4" fill="#f1f5f9" />
          <circle cx="14" cy="14" r="7" fill="#94a3b8" />
          <text x="30" y="18" fontSize="11" fill="#0f172a" fontFamily="Arial">Negar</text>
        </g>
        <AnimatedPulse cx={200} cy={142} r={18} color="#16a34a" />
      </>
    )}
    {/* Home indicator */}
    <rect x="170" y="222" width="60" height="4" rx="2" fill="#94a3b8" />
  </svg>
);

/* ---------- STEP DEFINITIONS BY PLATFORM ---------- */

function getSteps(platform) {
  if (platform === "ios-safari") {
    return [
      {
        title: "1. Sair do site e abrir Definições do iPhone",
        text: "Feche esta app (ou minimize) e abra o icon de Definições.",
        illustration: <IosSettingsSvg step={1} />,
      },
      {
        title: "2. Aceder a Safari",
        text: "Desça até encontrar 'Safari' e toque para abrir.",
        illustration: <IosSettingsSvg step={1} />,
      },
      {
        title: "3. Escolher Câmara",
        text: "Na secção 'Definições Sites', toque em 'Câmara'.",
        illustration: <IosSettingsSvg step={2} />,
      },
      {
        title: "4. Escolher 'Permitir'",
        text: "Selecione 'Permitir'. Volte à app e recarregue a página.",
        illustration: <IosSettingsSvg step={3} />,
      },
    ];
  }
  if (platform === "ios-chrome") {
    return [
      {
        title: "1. Abrir Definições do iPhone",
        text: "Feche o Chrome e abra o icon de Definições.",
        illustration: <IosSettingsSvg step={1} />,
      },
      {
        title: "2. Procurar 'Chrome' e abrir",
        text: "Desça até 'Chrome' na lista de apps.",
        illustration: <IosSettingsSvg step={1} />,
      },
      {
        title: "3. Ativar Câmara",
        text: "Na lista de permissões, ative o interruptor da 'Câmara'.",
        illustration: <IosSettingsSvg step={3} />,
      },
    ];
  }
  if (platform === "android-chrome") {
    return [
      {
        title: "1. Toque no cadeado 🔒",
        text: "À esquerda do endereço 'plastiq-mes.emergent.host', toque no cadeado.",
        illustration: <BrowserAddressBarSvg highlight="lock" />,
      },
      {
        title: "2. Abrir 'Permissões'",
        text: "No menu que aparece, escolha 'Permissões' (ou 'Definições do site').",
        illustration: <PermissionDropdownSvg highlight="camera" />,
      },
      {
        title: "3. Definir Câmara como 'Permitir'",
        text: "Toque em Câmara e escolha 'Permitir'. Depois recarregue a página.",
        illustration: <PermissionDropdownSvg highlight="camera" />,
      },
    ];
  }
  if (platform === "android-other") {
    return [
      {
        title: "1. Abrir menu do browser",
        text: "Toque nos três pontos (⋮) no canto superior direito.",
        illustration: <BrowserAddressBarSvg highlight="lock" />,
      },
      {
        title: "2. Definições do site",
        text: "Escolha 'Definições do site' ou 'Permissões'.",
        illustration: <PermissionDropdownSvg highlight="camera" />,
      },
      {
        title: "3. Câmara → Permitir",
        text: "Selecione Câmara e mude para 'Permitir'. Recarregue a página.",
        illustration: <PermissionDropdownSvg highlight="camera" />,
      },
    ];
  }
  // Desktop (Chrome, Edge, Firefox, Safari macOS)
  return [
    {
      title: "1. Clique no cadeado 🔒 (barra de endereço)",
      text: "À esquerda do endereço 'plastiq-mes.emergent.host', clique no cadeado.",
      illustration: <BrowserAddressBarSvg highlight="lock" />,
    },
    {
      title: "2. Abrir 'Definições do site'",
      text: "Aparece um menu — escolha 'Definições do site' ou 'Permissões'.",
      illustration: <PermissionDropdownSvg highlight="camera" />,
    },
    {
      title: "3. Câmara → 'Permitir'",
      text: "Na linha da Câmara, mude para 'Permitir'. Volte a esta página e recarregue.",
      illustration: <PermissionDropdownSvg highlight="camera" />,
    },
  ];
}

/* ---------- MAIN COMPONENT ---------- */

export function CameraPermissionGuideButton({ className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className={`border-blue-300 text-blue-700 ${className}`}
        data-testid="camera-help-btn"
      >
        <HelpCircle className="h-4 w-4 mr-1" /> Como desbloquear?
      </Button>
      <CameraPermissionGuide open={open} onOpenChange={setOpen} />
    </>
  );
}

export default function CameraPermissionGuide({ open, onOpenChange }) {
  const [platform] = useState(() => detectPlatform());
  const [step, setStep] = useState(0);
  const steps = getSteps(platform);
  const current = steps[step];
  const isLast = step === steps.length - 1;

  const platformLabel = {
    "ios-safari": "iPhone / iPad — Safari",
    "ios-chrome": "iPhone / iPad — Chrome",
    "android-chrome": "Android — Chrome",
    "android-other": "Android — outro browser",
    "desktop-chrome": "Computador — Chrome",
    "desktop-edge": "Computador — Edge",
    "desktop-firefox": "Computador — Firefox",
    "mac-safari": "Mac — Safari",
  }[platform] || "Computador";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="camera-guide-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-blue-600" />
            Como desbloquear a câmara
          </DialogTitle>
          <div className="text-xs text-muted-foreground">
            Instruções para <b>{platformLabel}</b>
          </div>
        </DialogHeader>

        <div className="mb-3 flex items-center gap-1">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded ${i <= step ? "bg-blue-600" : "bg-slate-200"}`}
            />
          ))}
        </div>

        <div className="rounded-lg bg-slate-50 p-4 border">
          {current.illustration}
        </div>

        <div className="mt-4">
          <h3 className="font-bold text-lg text-slate-900">{current.title}</h3>
          <p className="text-sm text-slate-700 mt-1">{current.text}</p>
        </div>

        <div className="flex justify-between items-center pt-4 border-t mt-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            data-testid="guide-prev"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Anterior
          </Button>
          <div className="text-xs text-muted-foreground">
            Passo {step + 1} / {steps.length}
          </div>
          {!isLast ? (
            <Button
              type="button"
              size="sm"
              onClick={() => setStep(Math.min(steps.length - 1, step + 1))}
              data-testid="guide-next"
            >
              Próximo <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              onClick={() => window.location.reload()}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="guide-reload"
            >
              <RefreshCw className="h-4 w-4 mr-1" /> Já permiti — Recarregar
            </Button>
          )}
        </div>

        <div className="text-xs text-slate-500 text-center pt-1 flex items-center justify-center gap-1">
          <ExternalLink className="h-3 w-3" />
          Se continuar bloqueado, tente em modo anónimo/privado ou reinstale a app.
        </div>
      </DialogContent>
    </Dialog>
  );
}
